import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '@hooks/useDispatch';
import { setEditingCell } from '@store/Spreadsheet/editingSlice';
import { updateRow } from '@store/Spreadsheet/rowsSlice';
import { CommonCellProps } from '../Cell';
import classes from '../Cell.module.css';
import { cellValueChanged } from '@store/Spreadsheet/cellChangesSlice';

export default function CellString({ rowId, columnId, isEditing, value, currentRow, initialValue }: CommonCellProps ) {
  const dispatch = useAppDispatch();
  /*
   * TV1-2358 — the editor's buffer.
   *
   * `CellString` is ONE component instance across both states: it renders a
   * <span> when idle and an <input> when editing, and is never unmounted in
   * between. So the `useState` initialiser runs at first paint of the cell, long
   * before anyone types — a seed cannot be delivered through it.
   *
   * Two effects, split by intent:
   *
   *  - Sync from the row, only while IDLE. An open editor owns its buffer; a
   *    paste or an undo landing on the row underneath must not silently rewrite
   *    what the user is typing (which the previous single unguarded effect did).
   *  - Seed on ENTERING edit mode. `initialValue` is the character that opened
   *    the editor (type-to-edit); with none, the edit starts from the cell's
   *    current value, as a double-click always has.
   */
  const [ localValue, setLocalValue ] = useState<string>( String( value ?? '' ));

  useEffect(() => {
    if ( isEditing ) return;
    setLocalValue( String( value ?? '' ));
  }, [ value, isEditing ]);

  useEffect(() => {
    if ( !isEditing ) return;
    setLocalValue( initialValue !== undefined ? initialValue : String( value ?? '' ));
    // Intentionally keyed on the edit session opening, NOT on `value`/`initialValue`:
    // re-running mid-edit would clobber what the user has typed since.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const commitEdit = useCallback(() => {
      if ( !currentRow ) return;
      const newValues = { ...currentRow.values, [columnId]: localValue };

      dispatch( updateRow({ id: rowId, changes: { values: newValues }}));
      dispatch( cellValueChanged({ rowId, columnId, oldValue: currentRow.values[columnId],newValue: localValue }));
      dispatch( setEditingCell( null ));
    },
    [ dispatch, rowId, columnId, currentRow, localValue ]);

  const cancelEdit = useCallback(() => {
    dispatch( setEditingCell( null ));
  }, [dispatch]);

  const handleKeyDown = useCallback(( e: React.KeyboardEvent<HTMLInputElement> ) => {
      if ( !isEditing ) return;
      if ( e.key === 'Enter' ) {
        commitEdit();
      } else if ( e.key === 'Escape' ) {
        cancelEdit();
      }
    }, [ isEditing, commitEdit, cancelEdit ]);


  if ( !isEditing ) {
    return <span className={classes.cellValue}>{value}</span>;
  }

  return (
    <input
      className={classes.cellStringInput}
      type="text"
      value={localValue}
      onChange={( e ) => setLocalValue( e.currentTarget.value )}
      onKeyDown={handleKeyDown}
      onBlur={commitEdit}
      autoFocus
    />
  );
}
